<template>
    <div class="col-12 mx-auto">
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="h5">
                    {{ $t('registers.events.eventsInCatalog') }}
                </div>
            </div>

            <div class="card-header" v-if="showNewButton || showSettingsBtn">
                <div class="btn-group">
                    <button type="button" class="btn btn-info btn-sm"
                            v-if="showNewButton && hasEditRights"
                            @click="createBtnClick">
                        {{$t('buttons.create.btnText')}}
                    </button>
                    <button type="button" class="btn btn-info btn-sm"
                            @click="goToExportTemplate"
                            v-if="isEventRegister && showImportFromExcelButton"
                            v-bind:disabled="eventExporting">
                        <i v-if="eventExporting" class="fa fa-spinner fa-pulse fa-fw"></i>
                        {{$t('buttons.exportTemplate.btnText')}}
                    </button>

                    <button type="button" class="btn btn-info btn-sm"
                            @click="selectImportFiles"
                            v-bind:disabled="eventImporting"
                            v-if="isEventRegister && showImportFromExcelButton">
                        <i v-if="eventImporting" class="fa fa-spinner fa-pulse fa-fw"></i>
                        {{$t('buttons.importFromExcel.btnText')}}
                    </button>

                    <input type="file"
                           id="importFile"
                           class="selectFile"
                           v-if="isEventRegister && showImportFromExcelButton"
                           @change="goToImportFromExcel"/>
                </div>

                <i class="fas fa-cogs pointer fa-2x float-right text-primary"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @userSettingsChanged="userSettingsChanged"
                                    @deleteGridSettings="deleteGridSettings">
                </grid-user-settings>
            </div>

            <div class="input-group col-lg-6 col-md-12 mt-3" role="toolbar">
                <input v-model="searchQuery" v-on:keyup.enter.stop="massSearch" v-on:keyup.esc.stop="clearFilters"
                       type="text" class="form-control" aria-label="Mass search input"
                       aria-describedby="massSearch">
                <button class="btn btn-secondary" @click.stop="massSearch">
                    <i class="fas fa-search"></i>
                    {{ $t('buttons.search.btnText') }}
                </button>
                <button class="btn btn-secondary"
                        @click.stop="clearFilters">
                    <i class="fas fa-times"></i>
                    {{ $t('buttons.clearFilters.btnText') }}
                </button>
            </div>


            <div class="card-body" id="grid">
                <kendo-datasource ref="datasource"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size='gridOptions.pageable.size'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError"
                                  :requestEnd="datasorceRequestEnd">
                </kendo-datasource>

                <kendo-grid ref="grid" id="grid1"
                            :data-source-ref="'datasource'"
                            :navigatable="gridOptions.navigatable"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :excel="gridOptions.excel"
                            :pdf="gridOptions.pdf"
                            :data-bound="dataBound">


                    <kendo-grid-column :field="'registerEntityId'"
                                       :title="$t('registers.events.gridHeaders.id')"
                                       :width="100"
                                       :lockable="true"
                                       :locked="true">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventName'"
                                       :title="$t('registers.events.gridHeaders.name')"
                                       :width="350"
                                       :template="eventNameCellTemplate"
                                       :lockable="true"
                                       :locked="true">
                    </kendo-grid-column>
                    <kendo-grid-column v-if="showBtns"
                                       :template="btnsTemplate"
                                       field="btnsColumn"
                                       :title="' '"
                                       :sortable="false"
                                       :filterable="false"
                                       :width="80"
                                       :lockable="true"
                                       :locked="true">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventCode'"
                                       :title="$t('registers.events.gridHeaders.eventCode')"
                                       :width="150"
                                       :template="eventCodeCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventStartDate'"
                                       :title="$t('registers.events.gridHeaders.eventStartDate')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventEndDate'"
                                       :title="$t('registers.events.gridHeaders.eventEndDate')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventSubscriptionStartDate'"
                                       :title="$t('registers.events.gridHeaders.eventSubscriptionStartDate')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventSubscriptionEndDate'"
                                       :title="$t('registers.events.gridHeaders.eventSubscriptionEndDate')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventDailyAllowanceFrom'"
                                       :title="$t('registers.events.gridHeaders.eventDailyAllowanceFrom')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventDailyAllowanceTo'"
                                       :title="$t('registers.events.gridHeaders.eventDailyAllowanceTo')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventStayFrom'"
                                       :title="$t('registers.events.gridHeaders.eventStayFrom')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventStayTo'"
                                       :title="$t('registers.events.gridHeaders.eventStayTo')"
                                       :width="150"
                                       :format="customDatetimeFormat"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'statusName'"
                                       :title="$t('registers.events.gridHeaders.statusName')"
                                       :width="150"
                                       :template="statusNameCellTemplate"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'statusNameEn'"
                                       :title="$t('registers.events.gridHeaders.statusName')"
                                       :width="150"
                                       :template="statusNameEnCellTemplate"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventForm'"
                                       :title="$t('registers.events.gridHeaders.eventForm')"
                                       :width="150"
                                       :template="eventFormCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventType'"
                                       :title="$t('registers.events.gridHeaders.eventType')"
                                       :width="150"
                                       :template="eventTypeCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventVenue'"
                                       :title="$t('registers.events.gridHeaders.eventVenue')"
                                       :width="150"
                                       :template="eventVenueCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventProjectName'"
                                       :title="$t('registers.events.gridHeaders.eventProjectName')"
                                       :width="150"
                                       :template="eventProjectNameCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventOrganizerName'"
                                       :title="$t('registers.events.gridHeaders.eventOrganizerName')"
                                       :width="150"
                                       :template="eventOrganizerNameCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'participantsMaxNum'"
                                       :title="$t('registers.events.gridHeaders.participantsMaxNum')"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventLocation'"
                                       :title="$t('registers.events.gridHeaders.eventLocation')"
                                       :width="150"
                                       :template="eventLocationCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>

                    <!--<kendo-grid-column :field="'eventParticipantsCount'"
                                   :title="$t('registers.events.gridHeaders.eventParticipantsCount')"
                                   :width="150"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>-->
                    <!--<kendo-grid-column :field="'employeePosition'"
                                   :title="$t('registers.events.gridHeaders.employeePosition')"
                                   :width="150"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>-->

                    <kendo-grid-column :field="'eventActivityDirection'"
                                       :title="$t('registers.events.gridHeaders.eventActivityDirection')"
                                       :width="150"
                                       :template="eventActivityDirectionCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>

                    <kendo-grid-column :field="'eventLegalArea'"
                                       :title="$t('registers.events.gridHeaders.eventLegalArea')"
                                       :width="150"
                                       :template="eventLegalAreaCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventFundingType'"
                                       :title="$t('registers.events.gridHeaders.eventFundingType')"
                                       :width="150"
                                       :template="eventFundingTypeCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventNote'"
                                       :title="$t('registers.events.gridHeaders.eventNote')"
                                       :width="150"
                                       :template="eventNoteCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventDirectorate'"
                                       :title="$t('registers.events.gridHeaders.eventDirectorate')"
                                       :width="150"
                                       :template="eventDirectorateCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventAudience'"
                                       :title="$t('registers.events.gridHeaders.eventAudience')"
                                       :width="150"
                                       :template="eventAudienceCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'thematicArea'"
                                       :title="$t('registers.events.gridHeaders.thematicArea')"
                                       :width="150"
                                       :template="thematicAreaCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'theme'"
                                       :title="$t('registers.events.gridHeaders.theme')"
                                       :width="150"
                                       :template="themeCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionOfEmployment'"
                                       :title="$t('registers.events.gridHeaders.institutionOfEmployment')"
                                       :width="150"
                                       :template="institutionOfEmploymentCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventSort'"
                                       :title="$t('registers.events.gridHeaders.eventSort')"
                                       :width="150"
                                       :template="eventSortCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventMoodleCategory'"
                                       :title="$t('registers.events.gridHeaders.eventMoodleCategory')"
                                       :width="150"
                                       :template="eventMoodleCategoryCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventDescription'"
                                       :title="$t('registers.events.gridHeaders.eventDescription')"
                                       :width="150"
                                       :template="eventDescriptionCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventExpectedResults'"
                                       :title="$t('registers.events.gridHeaders.eventExpectedResults')"
                                       :width="150"
                                       :template="eventExpectedResultsCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventLearningGoals'"
                                       :title="$t('registers.events.gridHeaders.eventLearningGoals')"
                                       :width="150"
                                       :template="eventLearningGoalsCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'useExtSubscriptionService'"
                                       :title="$t('registers.events.gridHeaders.useExtSubscriptionService')"
                                       :width="150"
                                       :template="extServiceCellTemplate"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'extSubscriptionService'"
                                       :title="$t('registers.events.gridHeaders.extSubscriptionService')"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventMigrationId'"
                                       :title="$t('registers.events.gridHeaders.eventMigrationId')"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>

                    <kendo-contextmenu @select="selectContextMenu" :target="'#grid1'" :filter="'td'">
                        <!--                        <li :id="'edit'" class="text-success"><i class="fas fa-edit"></i> {{ $t('buttons.edit.btnText') }}</li>-->
                        <!--                        <li :id="'editNewTab'" class="text-primary"><i class="fas fa-pencil-alt"></i> {{ $t('buttons.editInNewTab.btnText') }}</li>-->
                        <li v-if="hasReadRights" :id="'detailsAll'" class="text-info"><i class="fas fa-eye"></i> {{
                            $t('buttons.details.btnText') }}
                        </li>
<!--                        <li v-if="hasReadRights" :id="'detailsActive'" class="text-info"><i class="fas fa-eye"></i> {{-->
<!--                            $t('buttons.detailsActive.btnText') }}-->
<!--                        </li>-->
<!--                        <li v-if="hasReadRights" :id="'detailsShowInDescription'" class="text-info"><i-->
<!--                            class="fas fa-eye"></i> {{ $t('buttons.detailsShowInDescription.btnText') }}-->
<!--                        </li>-->
                        <!--                        <li v-if="hasDeleteRights" :id="'delete'" class="text-danger"><i class="far fa-trash-alt"></i> {{ $t('buttons.delete.btnText') }}</li>-->
                        <!--                        <li :id="'pdf'" class="text-danger"><i class="far fa-file-pdf"></i> {{ $t('buttons.pdfExport.btnText') }}</li>-->
                        <!--                        <li :id="'excel'" class="text-success"><i class="far fa-file-excel"></i> {{ $t('buttons.excelExport.btnText') }}</li>-->
                        <!--                        <li v-if="hasDeleteRights" :id="'archive'" class="text-secondary"><i class="far fa-file-archive"> </i>{{ $t('buttons.archive.btnText') }}</li>-->
                    </kendo-contextmenu>
                </kendo-grid>
            </div>
        </div>

        <DetailsModal v-model="detailsModel" :show="showDetails" @onClose="onDetailsClose"></DetailsModal>
    </div>

</template>
<script src="https://kendo.cdn.telerik.com/2019.2.619/js/pako_deflate.min.js"></script>

<script>
    import Vue from 'vue';
    import JSZip from 'jszip'
    import GridUserSettings from "../../../components/grid/UserSettings";
    import BtnsTemplate from "../../../components/grid/StandardBtnsTemplate";
    import { Culture, DynamicRegister, RegisterFieldValueQueryFilter, KendoGridPageSize} from '../../../models/enums';
    import {EventsOutOfCatalogGridSettingsModel} from '../../../models/models';
    import personalizationService from '../../../services/personalization.service';
    import DatetimeCellDisplayTemplate from "../../../components/grid/DatetimeCellDisplayTemplate";
    import EventsControls from './EventsControls'
    import actionLogService from "../../../services/actionLog.service"
    import DetailsModal from '../../../components/dynamic/register/details.modal';
    import {mapGetters} from 'vuex'

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });

    export default {
        name: 'EventsInAnnualCatalogGrid',
        components: {
            'grid-user-settings': GridUserSettings,
            EventsControls,
            DetailsModal
        },
        created: function () {
            window.JSZip = JSZip
        },
        props: {
            showHideButton: {
                default: false,
                type: Boolean
            },
            showPinButton: {
                default: false,
                type: Boolean
            },
            showNewButton: {
                default: true,
                type: Boolean
            },
            showResizeIcon: {
                default: true,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            showImportFromExcelButton: {
                default: false,
                type: Boolean
            },
            titleKey: {
                default: String
            },
            registerName: {
                default: '',
                type: String
            },
            annualId: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                componentName: "EventsListGrid",
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                titleKeyStr: 'registers.events.eventsOutOfCatalog',
                eventImporting: false,
                eventExporting: false,
                //eventsListUserSettings: new EventsOutOfCatalogGridSettingsModel(),
                searchQuery: '',
                schemaModelFields: {
                    registerEntityId: {
                        name: 'registerEntityId',
                        type: 'number',
                        editable: false,
                        nullable: false,
                        visible: true
                    },
                    eventName: {name: 'eventName', type: 'string', editable: false, nullable: true, visible: true},
                    eventCode: {name: 'eventCode', type: 'string', editable: false, nullable: true, visible: true},
                    eventStartDate: {
                        name: 'eventStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventEndDate: {name: 'eventEndDate', type: 'date', editable: false, nullable: true, visible: true},
                    eventSubscriptionStartDate: {
                        name: 'eventSubscriptionStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventSubscriptionEndDate: {
                        name: 'eventSubscriptionEndDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    statusName: {name: 'statusName', type: 'string', editable: false, nullable: true, visible: true},
                    statusNameEn: {
                        name: 'statusNameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventForm: {name: 'eventForm', type: 'string', editable: false, nullable: true, visible: true},
                    eventType: {name: 'eventType', type: 'string', editable: false, nullable: true, visible: true},
                    isEvent: {name: 'isEvent', type: 'boolean', editable: false, nullable: true, visible: true},
                    excludeFromReporting: {
                        name: 'excludeFromReporting',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    isPublic: {name: 'isPublic', type: 'boolean', editable: false, nullable: true, visible: true},
                    isLockedForEdit: {
                        name: 'isLockedForEdit',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    syncedWithMoodle: {
                        name: 'syncedWithMoodle',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    isCompleted: {name: 'isCompleted', type: 'boolean', editable: false, nullable: true, visible: true},
                    participantsMaxNum: {
                        name: 'participantsMaxNum',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventDailyAllowanceFrom: {
                        name: 'eventDailyAllowanceFrom',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventDailyAllowanceTo: {
                        name: 'eventDailyAllowanceTo',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventStayFrom: {
                        name: 'eventStayFrom',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventStayTo: {name: 'eventStayTo', type: 'date', editable: false, nullable: true, visible: true},
                    eventLocation: {
                        name: 'eventLocation',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventParticipantsCount: {
                        name: 'eventParticipantsCount',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventStatus: {name: 'eventStatus', type: 'string', editable: false, nullable: true, visible: true},
                    employeePosition: {
                        name: 'employeePosition',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventLegalArea: {
                        name: 'eventLegalArea',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventFundingType: {
                        name: 'eventFundingType',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventNote: {name: 'eventNote', type: 'string', editable: false, nullable: true, visible: true},
                    eventDirectorate: {
                        name: 'eventDirectorate',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventAudience: {
                        name: 'eventAudience',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    thematicArea: {
                        name: 'thematicArea',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    theme: {name: 'theme', type: 'string', editable: false, nullable: true, visible: true},
                    institutionOfEmployment: {
                        name: 'institutionOfEmployment',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventSort: {name: 'eventSort', type: 'string', editable: false, nullable: true, visible: true},
                    eventActivityDirection: {
                        name: 'eventActivityDirection',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventMoodleCategory: {
                        name: 'eventMoodleCategory',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventVenue: {name: 'eventVenue', type: 'string', editable: false, nullable: true, visible: true},
                    eventProjectName: {
                        name: 'eventProjectName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventOrganizerName: {
                        name: 'eventOrganizerName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventDescription: {
                        name: 'eventDescription',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventExpectedResults: {
                        name: 'eventExpectedResults',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventLearningGoals: {
                        name: 'eventLearningGoals',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventMigrationId: {
                        name: 'eventMigrationId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    deactivated: {name: 'deactivated', type: 'boolean', editable: false, nullable: true, visible: true},
                    deleted: {name: 'deleted', type: 'boolean', editable: false, nullable: true, visible: true},
                    archived: {name: 'archived', type: 'boolean', editable: false, nullable: true, visible: true},
                    useExtSubscriptionService: {
                        name: 'useExtSubscriptionService',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    extSubscriptionService: {
                        name: 'extSubscriptionService',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                },
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/events/listInCatalog`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    excel: {
                        fileName: 'Events.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: 'Events.pdf',
                        title: this.$t(this.titleKeyStr),
                        subject: this.$t(this.titleKeyStr),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: {top: '2cm', left: '1cm', right: '1cm', bottom: '1cm'},
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                },
                working: false,
                showUserSettingsForm: false,
                detailsModel: null,
                showDetails: false,
            }
        },
        methods: {
            dataBound(e) {
                if (!this.customSettingsLoaded) {
                    this.customSettingsLoaded = true;
                    this.loadGridSettings();
                }

                //if (this.showMasterDetails) {
                //    e.sender.expandRow(v.sender.tbody.find('tr.k-master-row').first())
                //}

                var items = e.sender.items();
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.statusNameEn === 'Active') {
                        $(item).addClass('left-border-green');
                    } else if (dataItem.statusNameEn === 'Reported') {
                        $(item).addClass('left-border-secondary');
                    }
                });
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return {models: kendo.stringify(options.models)}
                } else {
                    this.saveState();
                    let newOptions = {annualCatalogId: this.annualId, request: options}
                    return kendo.stringify(newOptions);
                }
            },
            //This is needed for grid state save and mass search
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();
                const grid = this.$refs.grid.kendoWidget();

                if (gridSettings && grid) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            })
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService
                                .mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size != grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }
                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (userSettings.pageable.size == KendoGridPageSize.All) {
                            grid.dataSource.pageSize(this.$refs["datasource"].kendoDataSource.total());
                        } else {
                            grid.dataSource.pageSize(userSettings.pageable.size);
                        }
                        grid.refresh();
                    } catch (e) {
                        return;
                    }
                }
            },
            saveState: function () {
                personalizationService.saveState(this.componentName, this.getGridOptions());
            },
            loadState: function () {
                let stateSettings = personalizationService.getState(this.componentName);
                let options = personalizationService.mergeStateSettings(this.getGridOptions(), stateSettings)
                if (options) {
                    let grid = this.$refs.grid.kendoWidget();
                    if (grid) {
                        grid.setOptions(options);
                    }

                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            resize(componentName, size) {
                this.$emit('resize', componentName, size, this.$refs.grid.kendoWidget())
            },
            hide(componentName) {
                this.$emit('hide', componentName)
            },
            pinned(componentName) {
                this.$emit('pinned', componentName)
            },
            unpinned(componentName) {
                this.$emit('unpinned', componentName)
            },
            datasorceRequestEnd(e) {
                if (e.type == "read" && e.response) {
                    // 	console.log(e.response[0]);
                    //alert(e.respondashboard-ctrlse[0].TaskID);
                }
            },
            datetimeDisplayTemplate(date) {
                return {
                    template: Vue.component(DatetimeCellDisplayTemplate.name, DatetimeCellDisplayTemplate),
                    templateArgs: {
                        dateValue: date,
                        showShortDate: false
                    },
                };
            },
            datetimeDisplayTemplateEventStartDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventStartDate);
            },
            datetimeDisplayTemplateEventEndDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventEndDate);
            },
            datetimeDisplayTemplateEventSubscriptionStartDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventSubscriptionStartDate);
            },
            datetimeDisplayTemplateEventSubscriptionEndDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventSubscriptionEndDate);
            },
            datetimeDisplayTemplateEventDailyAllowanceFrom: function (e) {
                return this.datetimeDisplayTemplate(e.eventDailyAllowanceFrom);
            },
            datetimeDisplayTemplateEventDailyAllowanceTo: function (e) {
                return this.datetimeDisplayTemplate(e.eventDailyAllowanceTo);
            },
            datetimeDisplayTemplateEventStayFrom: function (e) {
                return this.datetimeDisplayTemplate(e.eventStayFrom);
            },
            datetimeDisplayTemplateEventStayTo: function (e) {
                return this.datetimeDisplayTemplate(e.eventStayTo);
            },
            refresh() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.read();
                }
            },
            clearFilters() {
                this.searchQuery = '';
                personalizationService.claerState(this.componentName);
                personalizationService.clearFilters(this.$refs.grid.kendoWidget());
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            createBtnClick() {
                this.$router.push('/registers/events/create')
            },
            editBtnClick(args) {
                let url = '/registers/events/edit/' + args.registerEntityId
                this.$router.push(url)
            },
            detailsAllBtnClick(args) {
                this.showDetailsModal(args, RegisterFieldValueQueryFilter.All);
            },
            detailsShowInDescriptionBtnClick(args) {
                this.showDetailsModal(args, RegisterFieldValueQueryFilter.ShowInDescription);
            },
            detailsActiveBtnClick(args) {
                this.showDetailsModal(args, RegisterFieldValueQueryFilter.Active);
            },
            editInNewTabBtnClick(args) {
                let routeData = this.$router.resolve({name: 'eventsEdit', params: {id: args.registerEntityId}})
                window.open(routeData.href, '_blank')
            },
            deleteBtnClick(args) {
                if (this.hasDeleteRights) {
                    // Изтриване
                    this.delete(args.registerEntityId, args.eventName)
                }
            },
            copyEventClick(args) {
                this.$router.push({
                    name: 'events',
                    params: {copyFromRegisterEntryId: args.registerEntityId}
                });
            },
            exportPdfBtnClick(args) {
                this.$http.get('/api/DynamicRegisters/events/pdfExport/' + args.registerEntityId, {responseType: 'blob'})
                    .then(response => {
                        const blob = new Blob([response.data], {type: 'application/pdf'});
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', this.registerName + '.pdf'); //or any other extension
                        document.body.appendChild(link);
                        link.click();
                    }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            exportExcelBtnClick(args) {
                this.$http.get('/api/DynamicRegisters/events/excelExport/' + args.registerEntityId, {responseType: 'blob'})
                    .then(response => {
                        const blob = new Blob([response.data], {type: 'application/vnd.ms-excel'});
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', this.registerName + '.xlsx'); //or any other extension
                        document.body.appendChild(link);
                        link.click();
                    }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            archiveBtnClick(args) {
                if (this.isEventRegister) {
                    this.archive(DynamicRegister.Events, args.registerEntityId, this.registerName.toLowerCase(), args.eventName)
                }
            },
            showDetailsModal(args, filter) {
                this.$api.dynamicRegister
                    .getDetails(args.registerEntityId, filter)
                    .then(response => {
                        const model = response.data;
                        if (model) {
                            this.detailsModel = model;
                            this.showDetails = true;
                        }
                    })
                    .catch(error => {
                        this.detailsModel = null;
                        this.showDetails = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: [
                            {
                                name: "edit",
                                text: this.$t('buttons.edit.btnText'),
                                tooltip: this.$t('buttons.edit.btnText'),
                                class: 'btn btn-success',
                                icon: "fas fa-edit",
                                show: true,
                                clickHandler: this.editBtnClick
                            },
                            {
                                name: "editInNewTab",
                                text: this.$t('buttons.editInNewTab.btnText'),
                                tooltip: this.$t('buttons.editInNewTab.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-pencil-alt",
                                show: true,
                                clickHandler: this.editInNewTabBtnClick
                            },
                            {
                                name: "delete",
                                text: this.$t('buttons.delete.btnText'),
                                tooltip: this.$t('buttons.delete.btnText'),
                                class: 'btn btn-danger',
                                icon: "far fa-trash-alt",
                                show: this.hasDeleteRights,
                                clickHandler: this.deleteBtnClick
                            },
                            {
                                name: "editInNewTab",
                                text: this.$t('buttons.copy.btnText'),
                                tooltip: this.$t('buttons.copy.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-copy",
                                show: true,
                                clickHandler: this.copyEventClick
                            },
                            {
                                name: "exportPdf",
                                text: this.$t('buttons.pdfExport.btnText'),
                                tooltip: this.$t('buttons.pdfExport.btnText'),
                                class: 'btn btn-danger',
                                icon: "far fa-file-pdf",
                                show: true,
                                clickHandler: this.exportPdfBtnClick
                            },
                            {
                                name: "exportExcel",
                                text: this.$t('buttons.excelExport.btnText'),
                                tooltip: this.$t('buttons.excelExport.btnText'),
                                class: 'btn btn-success',
                                icon: "far fa-file-excel",
                                show: true,
                                clickHandler: this.exportExcelBtnClick
                            },
                            {
                                name: "archive",
                                text: this.$t('buttons.archive.btnText'),
                                tooltip: this.$t('buttons.archive.btnText'),
                                class: 'btn btn-secondary',
                                icon: "far fa-file-archive",
                                show: this.hasDeleteRights,
                                clickHandler: this.archiveBtnClick
                            }
                        ]
                    }
                };
            },
            selectContextMenu(e) {
                var btnId = e.item.id;
                var index = e.target.parentElement.rowIndex;
                var row = this.$refs.grid.kendoWidget().dataSource.data()[index]
                var id = row.registerEntityId;

                if (btnId === 'edit') {
                    // Редакция
                    this.editBtnClick({registerEntityId: id});

                } else if (btnId === 'editNewTab') {
                    this.editInNewTabBtnClick({registerEntityId: id})

                } else if (btnId === 'detailsAll') {
                    this.detailsAllBtnClick({registerEntityId: id})
                } else if (btnId === 'detailsActive') {
                    this.detailsActiveBtnClick({registerEntityId: id})
                } else if (btnId === 'detailsShowInDescription') {
                    this.detailsShowInDescriptionBtnClick({registerEntityId: id})
                } else if (btnId === 'delete') {
                    if (this.hasDeleteRights) {
                        // Изтриване
                        this.deleteBtnClick({registerEntityId: id, eventName: row.eventName})
                    }

                } else if (btnId === 'pdf') {
                    this.exportPdfBtnClick({registerEntityId: id})

                } else if (btnId === 'excel') {
                    this.exportExcelBtnClick({registerEntityId: id})

                } else if (btnId === 'archive') {
                    // Експорт на архив (JSON на модела на сибитие)
                    if (this.isEventRegister) {
                        this.archive(DynamicRegister.Events, id, this.registerName.toLowerCase(), row.eventName)
                    }
                }
            },
            delete(id, eventName) {
                kendo.confirm(`${this.$t('registers.events.confirmations.delete')} '${id}/${eventName}'?`)
                    .then(function () {
                        this.working = true;
                        this.$http.delete('/api/dynamicRegisterEntry/delete',
                            {params: {id: id}}
                        ).then((response) => {
                            actionLogService.eventDelete(id);

                            this.$refs.grid.kendoWidget().dataSource.read();
                            this.$vs.notify({
                                title: this.$t('buttons.delete.action'),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration,
                            })
                        }).catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: 'danger',
                                time: this.$store.getters.notificationDuration
                            })
                        })

                        this.working = false;
                    }.bind(this));
            },
            archive(registerId, registerEntityId, registerName, eventName) {
                kendo.confirm(`${this.$t('registers.events.confirmations.archive')} '${registerEntityId}/${eventName}'?`)
                    .then(function () {

                        this.$http.delete('/api/events/archive',
                            {params: {id: registerEntityId}}
                        ).then((response) => {
                            actionLogService.eventArchive(registerEntityId);

                            this.refresh();
                            this.$vs.notify({
                                title: this.$t('buttons.archive.btnText'),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration,
                            })

                            // Експорт на архив (JSON на модела на сибитие)
                            this.$http.get(`/api/dynamicRegisters/archive`, {
                                params: {
                                    registerId: registerId,
                                    registerEntityId: registerEntityId,
                                    registerName: registerName
                                }, responseType: 'blob'
                            }).then(response => {
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', 'archive.txt'); //or any other extension
                                document.body.appendChild(link);
                                link.click();
                            }).catch(error => {
                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                    text: this.$helpers.errorText(error),
                                    color: 'danger',
                                    time: this.$store.getters.notificationDuration
                                });
                            });

                        }).catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: 'danger',
                                time: this.$store.getters.notificationDuration
                            })
                        })


                    }.bind(this));
            },
            goToExportTemplate() {
                this.eventExporting = true;

                this.$http.get('/api/dynamicRegisters/exportEventsTemplate/', {
                    parameters: {
                        registerEntityId: this.entryId,
                        withData: false
                    },
                    responseType: 'blob'
                })
                    .then(response => {
                        this.eventExporting = false;

                        const blob = new Blob([response.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'Events.xlsx'); //or any other extension
                        document.body.appendChild(link);
                        link.click();
                    }).catch(error => {
                    this.eventExporting = false;

                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            selectImportFiles() {
                var dialog = $("#importFile");
                dialog.trigger("click");
            },
            goToImportFromExcel(args) {
                this.eventImporting = true;

                var $input = $("#importFile")[0];
                var files = $input.files;
                if (files && files.length > 0) {
                    var filename = files[0].name;
                    var file = files[0];

                    // clear the selected file so that the change event triggers when the same file is selected
                    $input.value = null;

                    var formData = new FormData();
                    formData.append('FileName', filename);
                    formData.append('FileContent', file);

                    this.$http.post('/api/dynamicRegisters/import', formData).then(response => {
                        this.eventImporting = false;
                        this.refresh();

                        if (response.data.hasErrors) {
                            this.$vs.notify({
                                title: this.$t('notifications.error'),
                                text: response.data.data, //this.$helpers.errorText(error),
                                color: 'danger',
                                time: this.$store.getters.notificationDuration
                            });
                        } else {
                            this.$vs.notify({
                                title: response.request ? this.$t('request.response.status.' + response.request.status) : this.$t('request.response.status.200'),
                                text: response.data.message ? response.data.message : (response.request.statusText ? response.request.statusText : response.message),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        }
                    }).catch(error => {
                        this.eventImporting = false;
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
                }
            },
            extServiceCellTemplate: function (dataItem) {
                let templateHtml =
                    '<span class="badge ' + (dataItem.useExtSubscriptionService ? 'badge-warning' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.useExtSubscriptionService ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            eventNameCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventName ? dataItem.eventName.replace('"', '\'') : '') + '">' + (dataItem.eventName ? dataItem.eventName : "") + '</span>';

                return templateHtml;
            },
            eventCodeCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventCode ? dataItem.eventCode.replace('"', '\'') : '') + '">' + (dataItem.eventCode ? dataItem.eventCode : "") + '</span>';

                return templateHtml;
            },
            eventDescriptionCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + dataItem.eventDescription + '">' + (dataItem.eventDescription ? dataItem.eventDescription : "") + '</span>';

                return templateHtml;
            },
            eventExpectedResultsCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventExpectedResults ? dataItem.eventExpectedResults.replace('"', '\'') : '') + '">' + (dataItem.eventExpectedResults ? dataItem.eventExpectedResults : "") + '</span>';

                return templateHtml;
            },
            eventLearningGoalsCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventLearningGoals ? dataItem.eventLearningGoals.replace('"', '\'') : '') + '">' + (dataItem.eventLearningGoals ? dataItem.eventLearningGoals : "") + '</span>';

                return templateHtml;
            },
            statusNameCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.statusName ? dataItem.statusName.replace('"', '\'') : '') + '">' + (dataItem.statusName ? dataItem.statusName : "") + '</span>';

                return templateHtml;
            },
            statusNameEnCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.statusNameEn ? dataItem.statusNameEn.replace('"', '\'') : '') + '">' + (dataItem.statusNameEn ? dataItem.statusNameEn : "") + '</span>';

                return templateHtml;
            },
            eventFormCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventForm ? dataItem.eventForm.replace('"', '\'') : '') + '">' + (dataItem.eventForm ? dataItem.eventForm : "") + '</span>';

                return templateHtml;
            },
            eventTypeCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventType ? dataItem.eventType.replace('"', '\'') : '') + '">' + (dataItem.eventType ? dataItem.eventType : "") + '</span>';

                return templateHtml;
            },
            eventVenueCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventVenue ? dataItem.eventVenue.replace('"', '\'') : '') + '">' + (dataItem.eventVenue ? dataItem.eventVenue : "") + '</span>';

                return templateHtml;
            },
            eventProjectNameCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventProjectName ? dataItem.eventProjectName.replace('"', '\'') : '') + '">' + (dataItem.eventProjectName ? dataItem.eventProjectName : "") + '</span>';

                return templateHtml;
            },
            eventOrganizerNameCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventOrganizerName ? dataItem.eventOrganizerName.replace('"', '\'') : '') + '">' + (dataItem.eventOrganizerName ? dataItem.eventOrganizerName : "") + '</span>';

                return templateHtml;
            },
            eventLocationCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventLocation ? dataItem.eventLocation.replace('"', '\'') : '') + '">' + (dataItem.eventLocation ? dataItem.eventLocation : "") + '</span>';

                return templateHtml;
            },
            eventActivityDirectionCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventActivityDirection ? dataItem.eventActivityDirection.replace('"', '\'') : '') + '">' + (dataItem.eventActivityDirection ? dataItem.eventActivityDirection : "") + '</span>';

                return templateHtml;
            },
            eventLegalAreaCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventLegalArea ? dataItem.eventLegalArea.replace('"', '\'') : '') + '">' + (dataItem.eventLegalArea ? dataItem.eventLegalArea : "") + '</span>';

                return templateHtml;
            },
            eventFundingTypeCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventFundingType ? dataItem.eventFundingType.replace('"', '\'') : '') + '">' + (dataItem.eventFundingType ? dataItem.eventFundingType : "") + '</span>';

                return templateHtml;
            },
            eventNoteCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventNote ? dataItem.eventNote.replace('"', '\'') : '') + '">' + (dataItem.eventNote ? dataItem.eventNote : "") + '</span>';

                return templateHtml;
            },
            eventDirectorateCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventDirectorate ? dataItem.eventDirectorate.replace('"', '\'') : '') + '">' + (dataItem.eventDirectorate ? dataItem.eventDirectorate : "") + '</span>';

                return templateHtml;
            },
            eventAudienceCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventAudience ? dataItem.eventAudience.replace('"', '\'') : '') + '">' + (dataItem.eventAudience ? dataItem.eventAudience : "") + '</span>';

                return templateHtml;
            },
            thematicAreaCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.thematicArea ? dataItem.thematicArea.replace('"', '\'') : '') + '">' + (dataItem.thematicArea ? dataItem.thematicArea : "") + '</span>';

                return templateHtml;
            },
            themeCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.theme ? dataItem.theme.replace('"', '\'') : '') + '">' + (dataItem.theme ? dataItem.theme : "") + '</span>';

                return templateHtml;
            },
            institutionOfEmploymentCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.institutionOfEmployment ? dataItem.institutionOfEmployment.replace('"', '\'') : '') + '">' + (dataItem.institutionOfEmployment ? dataItem.institutionOfEmployment : "") + '</span>';

                return templateHtml;
            },
            eventSortCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventSort ? dataItem.eventSort.replace('"', '\'') : '') + '">' + (dataItem.eventSort ? dataItem.eventSort : "") + '</span>';

                return templateHtml;
            },
            eventMoodleCategoryCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventMoodleCategory ? dataItem.eventMoodleCategory.replace('"', '\'') : '') + '">' + (dataItem.eventMoodleCategory ? dataItem.eventMoodleCategory : "") + '</span>';

                return templateHtml;
            },
            settingsReloaded() {

            },
            onDetailsClose() {
                this.showDetails = false;
                this.detailsModel = null;
            },
        },
        mounted() {
            var container = $(this.$el);
            container.find('[data-toggle="tooltip"]').tooltip({container});
        },
        computed: {
            ...mapGetters(['dateTimeFormat']),
            customDatetimeFormat() {
                return `{0:${this.dateTimeFormat}}`;
            },
            token: function () {
                return this.$store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
            hasEditRights() {
                return this.$store.getters.claims.event.create;
            },
            hasDeleteRights() {
                return this.$store.getters.claims.event.delete;
            },
            hasReadRights() {
                return this.$store.getters.claims.event.read;
            },
            isEventRegister() {
                return true;
            },
            toolbar: function () {
                return []
            }
        },
        watch: {
            annualId: {
                immediate: true,
                handler(newVal) {
                    if (newVal) {
                        this.annualId = newVal;
                        this.refresh();
                    }
                }
            },
            customSettingsLoaded: function (val) {
                if (val === true) {
                    //this.loadState();
                }
            }
        }
    }
</script>


<style>
    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: "DejaVu Sans", "Arial", sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit, .k-grid-details, .k-grid-delete, .k-grid-pdf, .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }
</style>

<style lang="scss">
    .grid-wrapper {
        overflow-x: auto;
    }

    tr.left-border-danger {
        &:hover {
            color: rgba(255, 0, 0, 0.7) !important;
        }

        td:first-child {
            border-left: 3px solid rgba(255, 0, 0, 0.5) !important;
        }

    }

    tr.left-border-blue {
        &:hover {
            color: #3498db !important;
        }

        td:first-child {
            border-left: 4px solid #3498db !important;
        }

    }

    tr.left-border-green {
        &:hover {
            color: #4caf50 !important;
        }

        td:first-child {
            border-left: 4px solid #4caf50 !important;
        }

    }

    tr.left-border-secondary {
        &:hover {
            color: #707a8a !important;
        }

        td:first-child {
            border-left: 4px solid #707a8a !important;
        }

    }

    .k-grid td {
        white-space: nowrap;
        text-overflow: ellipsis;
    }
</style>
